import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = ["data-testid"]
const _hoisted_2 = ["data-testid"]
const _hoisted_3 = ["data-testid"]
const _hoisted_4 = ["data-testid", "disabled"]
const _hoisted_5 = ["data-testid", "disabled"]
const _hoisted_6 = ["data-testid", "aria-label"]
const _hoisted_7 = { key: 0 }

import { computed, ref } from 'vue'
import { mdiDotsHorizontal } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'
import ActionSheet from '@/components/designSystem/ActionSheet/ActionSheet.vue'
import type { ActionSheetItem } from '@/components/designSystem/ActionSheet/types'
import type {
  StationDetailAction,
  StationDetailActionId
} from '../composables/useStationDetailActions'

const MORE_LABEL = 'Mais ações'


export default /*#__PURE__*/_defineComponent({
  __name: 'StationDetailsDock',
  props: {
    actions: {},
    testId: { default: 'station-details-dock' }
  },
  setup(__props: any) {

const props = __props;

/**
 * Dock fixo do Detalhes do Plantão no celular (spec 2026-09-30 §5 R6): a primária
 * ("Alterar/Alocar/Identificar médico", ou "Editar plantão" sem smart allocation),
 * "Enviar registro" em SEGUNDO (Q35) e "⋯" com o resto num ActionSheet. Tudo sai da
 * lista do `useStationDetailActions` — as condições são as mesmas do desktop.
 */


/** Ordem do mockup R6 (Editar, Histórico, …) com a ação destrutiva por último. */
const SHEET_ORDER: StationDetailActionId[] = [
  'edit',
  'history',
  'exceptionality',
  'check-attempts',
  'remove'
]

const isSheetOpen = ref(false)

const primary = computed(() => props.actions.find((a) => a.placement === 'primary'))
const secondary = computed(() => props.actions.find((a) => a.placement === 'secondary'))

/** "⋯" é o único botão do dock: vira botão de largura cheia com rótulo. */
const isMoreAlone = computed(() => !primary.value && !secondary.value)

/**
 * Motivos distintos das ações do dock travadas, uma linha por motivo. Com um motivo
 * só (ou o mesmo nas duas), a linha é o motivo puro; com dois diferentes, cada linha
 * leva o rótulo do botão para dizer qual trava é de qual.
 */
const hintLines = computed(() => {
  const locked = [primary.value, secondary.value].filter(
    (a): a is StationDetailAction => !!a?.disabled && !!a.disabledReason
  )
  const reasons = [...new Set(locked.map((a) => a.disabledReason as string))]
  if (reasons.length <= 1) return reasons
  return locked.map((a) => `${a.label}: ${a.disabledReason}`)
})

/** Posição no sheet; id fora da lista conhecida vai para o fim. */
const sheetRank = (id: StationDetailActionId) => {
  const index = SHEET_ORDER.indexOf(id)
  return index === -1 ? Infinity : index
}

const sheetActions = computed(() =>
  props.actions
    .filter((a) => a.placement === 'overflow' || a.placement === 'standalone')
    .sort((a, b) => sheetRank(a.id) - sheetRank(b.id))
)

const sheetItems = computed<ActionSheetItem[]>(() =>
  sheetActions.value.map((a) => ({
    id: a.id,
    label: a.label,
    icon: a.icon,
    danger: a.danger,
    disabled: a.disabled,
    disabledReason: a.disabledReason,
    description: a.disabled && a.disabledReason ? a.disabledReason : a.description
  }))
)

function onSelect(id: string) {
  const selected = sheetActions.value.find((a) => a.id === id)
  if (!selected || selected.disabled) return
  selected.handler()
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    "data-testid": _ctx.testId,
    class: "flex flex-col gap-2"
  }, [
    (hintLines.value.length)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          "data-testid": `${_ctx.testId}-hint`,
          class: "flex flex-col gap-0.5 text-left text-xs text-gray-600"
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(hintLines.value, (line) => {
            return (_openBlock(), _createElementBlock("p", { key: line }, _toDisplayString(line), 1))
          }), 128))
        ], 8, _hoisted_2))
      : _createCommentVNode("", true),
    _createElementVNode("div", {
      "data-testid": `${_ctx.testId}-buttons`,
      class: "flex items-center gap-2"
    }, [
      (primary.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            type: "button",
            "data-testid": `${_ctx.testId}-primary`,
            disabled: primary.value.disabled,
            class: "h-11 min-w-0 flex-1 rounded-lg bg-primary30 px-3 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-50",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (primary.value.handler()))
          }, _toDisplayString(primary.value.label), 9, _hoisted_4))
        : _createCommentVNode("", true),
      (secondary.value)
        ? (_openBlock(), _createElementBlock("button", {
            key: 1,
            type: "button",
            "data-testid": `${_ctx.testId}-secondary`,
            disabled: secondary.value.disabled,
            class: "h-11 min-w-0 flex-1 rounded-lg border-2 border-solid border-primary30 bg-white px-3 text-sm font-bold text-primary30 disabled:cursor-not-allowed disabled:opacity-50",
            onClick: _cache[1] || (_cache[1] = ($event: any) => (secondary.value.handler()))
          }, _toDisplayString(secondary.value.label), 9, _hoisted_5))
        : _createCommentVNode("", true),
      (sheetItems.value.length)
        ? (_openBlock(), _createElementBlock("button", {
            key: 2,
            type: "button",
            "data-testid": `${_ctx.testId}-more`,
            "aria-label": isMoreAlone.value ? undefined : MORE_LABEL,
            class: _normalizeClass(["flex h-11 items-center justify-center gap-2 rounded-lg border-2 border-solid border-primary30 bg-white text-primary30", isMoreAlone.value ? 'min-w-0 flex-1 px-3 text-sm font-bold' : 'w-11 shrink-0']),
            onClick: _cache[2] || (_cache[2] = ($event: any) => (isSheetOpen.value = true))
          }, [
            _createVNode(SVGIcon, {
              path: _unref(mdiDotsHorizontal),
              class: "h-5 w-5 shrink-0",
              "aria-hidden": "true"
            }, null, 8, ["path"]),
            (isMoreAlone.value)
              ? (_openBlock(), _createElementBlock("span", _hoisted_7, _toDisplayString(MORE_LABEL)))
              : _createCommentVNode("", true)
          ], 10, _hoisted_6))
        : _createCommentVNode("", true)
    ], 8, _hoisted_3),
    _createVNode(ActionSheet, {
      modelValue: isSheetOpen.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((isSheetOpen).value = $event)),
      title: "Ações do plantão",
      items: sheetItems.value,
      "test-id": "station-details-actions",
      onSelect: onSelect
    }, null, 8, ["modelValue", "items"])
  ], 8, _hoisted_1))
}
}

})